import { UserRole } from '../../models';
import { RBACAccessMatrixModel } from '../../models/rbac-access-matrix';
import { resourceType } from './RBACResourceType';
import { RBACRoleType } from './RBACRoleType';

const accessForAllTrends = {
  [resourceType.Trends]: RBACRoleType.AllAccess,
  [resourceType.TrendsEarningRateToggle]: RBACRoleType.AllAccess,
  [resourceType.TrendsMainStat]: RBACRoleType.AllAccess,
  [resourceType.TrendsMainUsersIPDStat]: RBACRoleType.AllAccess,
  [resourceType.TrendsOffers]: RBACRoleType.AllAccess,
  [resourceType.TrendsRTB]: RBACRoleType.AllAccess,
  [resourceType.TrendsManagers]: RBACRoleType.AllAccess,
  [resourceType.TrendsHosts]: RBACRoleType.AllAccess,
  [resourceType.TrendsAllHosts]: RBACRoleType.AllAccess,
  [resourceType.TrendsPackages]: RBACRoleType.AllAccess,
};

const getAdminSupervisorRBACMatrix = (): RBACAccessMatrixModel => ({
  [resourceType.Admin]: RBACRoleType.AllAccess,
  [resourceType.Settings]: RBACRoleType.AllAccess,
  [resourceType.CreateUser]: RBACRoleType.AllAccess,
  [resourceType.Users]: RBACRoleType.AllAccess,
  [resourceType.CountryStatistic]: RBACRoleType.AllAccess,
  [resourceType.OfferBookCountryStatistic]: RBACRoleType.AllAccess,
  [resourceType.RtbCountryStatistic]: RBACRoleType.AllAccess,
  [resourceType.StatisticComparisonByDays]: RBACRoleType.AllAccess,
  [resourceType.Partners]: RBACRoleType.AllAccess,
  [resourceType.Offer]: RBACRoleType.ReadUpdateDelete,
  [resourceType.OfferMainData]: RBACRoleType.AllAccess,
  [resourceType.FilterByUser]: RBACRoleType.HasAccess,
  [resourceType.OfferActions]: RBACRoleType.HasAccess,
  [resourceType.PartnerActions]: RBACRoleType.HasAccess,
  [resourceType.OfferSummaryStatistic]: RBACRoleType.HasAccess,
  [resourceType.RTBSummaryStatistic]: RBACRoleType.HasAccess,
  [resourceType.HostSummaryStatistic]: RBACRoleType.HasAccess,
  [resourceType.RtbStatistic]: RBACRoleType.Read,
  [resourceType.Hosts]: RBACRoleType.Read,
  [resourceType.HostPublisherFilter]: RBACRoleType.Read,
  [resourceType.FullStatisticData]: RBACRoleType.Read,
  [resourceType.FullStatisticWithRatesData]: RBACRoleType.Read,
  [resourceType.Ltv]: RBACRoleType.Read,
  ...accessForAllTrends,
});

const getModeratorRBACMatrix = (): RBACAccessMatrixModel => {
  return {
    [resourceType.Settings]: RBACRoleType.NoAccess,
    [resourceType.Users]: RBACRoleType.Read,
    [resourceType.CountryStatistic]: RBACRoleType.AllAccess,
    [resourceType.OfferBookCountryStatistic]: RBACRoleType.AllAccess,
    [resourceType.RtbCountryStatistic]: RBACRoleType.AllAccess,
    [resourceType.Partners]: RBACRoleType.AllAccess,
    [resourceType.Offer]: RBACRoleType.AllAccess,
    [resourceType.OfferMainData]: RBACRoleType.AllAccess,
    [resourceType.FilterByUser]: RBACRoleType.HasAccess,
    [resourceType.OfferActions]: RBACRoleType.HasAccess,
    [resourceType.PartnerActions]: RBACRoleType.HasAccess,
    [resourceType.OfferSummaryStatistic]: RBACRoleType.HasAccess,
    [resourceType.RTBSummaryStatistic]: RBACRoleType.HasAccess,
    [resourceType.HostSummaryStatistic]: RBACRoleType.HasAccess,
    [resourceType.RtbStatistic]: RBACRoleType.Read,
    [resourceType.Hosts]: RBACRoleType.Read,
    [resourceType.HostPublisherFilter]: RBACRoleType.Read,
    [resourceType.FullStatisticData]: RBACRoleType.Read,
    [resourceType.FullStatisticWithRatesData]: RBACRoleType.Read,
    [resourceType.Ltv]: RBACRoleType.Read,
    [resourceType.StatisticComparisonByDays]: RBACRoleType.AllAccess,
    ...accessForAllTrends,
  };
};

const getManagerRBACMatrix = (): RBACAccessMatrixModel => {
  return {
    [resourceType.Settings]: RBACRoleType.NoAccess,
    [resourceType.Partners]: RBACRoleType.ReadCreate,
    [resourceType.Users]: RBACRoleType.NoAccess,
    [resourceType.Offer]: RBACRoleType.CreateReadUpdate,
    [resourceType.OfferMainData]: RBACRoleType.ReadCreate,
    [resourceType.CountryStatistic]: RBACRoleType.AllAccess,
    [resourceType.OfferBookCountryStatistic]: RBACRoleType.AllAccess,
    [resourceType.OfferActions]: RBACRoleType.HasAccess,
    [resourceType.PartnerActions]: RBACRoleType.HasAccess,
    [resourceType.OfferSummaryStatistic]: RBACRoleType.HasAccess,
    [resourceType.RTBSummaryStatistic]: RBACRoleType.HasAccess,
    [resourceType.HostSummaryStatistic]: RBACRoleType.HasAccess,
    [resourceType.StatisticComparisonByDays]: RBACRoleType.AllAccess,
    // ...accessForAllTrends,
  };
};

const getAdvertiserRBACMatrix = (): RBACAccessMatrixModel => {
  return {
    [resourceType.Offer]: RBACRoleType.Read,
    [resourceType.CountryStatistic]: RBACRoleType.Read,
    [resourceType.OfferBookCountryStatistic]: RBACRoleType.AllAccess,
    [resourceType.RtbCountryStatistic]: RBACRoleType.AllAccess,
    // [resourceType.Partners]: RBACRoleType.Read,
    [resourceType.RtbStatistic]: RBACRoleType.Read,
    // [resourceType.StatisticComparisonByDays]: RBACRoleType.AllAccess,
  };
};

const getAnalystRBACMatrix = (): RBACAccessMatrixModel => {
  return {
    [resourceType.Hosts]: RBACRoleType.Read,
    // [resourceType.FullStatisticData]: RBACRoleType.Read, // commented by GT-
    [resourceType.Trends]: RBACRoleType.Read,
    [resourceType.TrendsHosts]: RBACRoleType.Read,
    // [resourceType.TrendsPackages]: RBACRoleType.Read, // commented by GT-211
    [resourceType.CountryStatistic]: RBACRoleType.Read,
    // [resourceType.OfferBookCountryStatistic]: RBACRoleType.AllAccess, // commented by GT-211
    // [resourceType.RtbCountryStatistic]: RBACRoleType.Read, // commented by GT-211
    // [resourceType.Ltv]: RBACRoleType.Read, // commented by GT-211
  };
};

const getDemoVisitorRBACMatrix = (): RBACAccessMatrixModel => {
  return {
    [resourceType.Hosts]: RBACRoleType.Read,
    [resourceType.HostSummaryStatistic]: RBACRoleType.HasAccess,
    [resourceType.Trends]: RBACRoleType.AllAccess,
    [resourceType.TrendsHosts]: RBACRoleType.AllAccess,
  };
};

const getPublisherVisitorRBACMatrix = (): RBACAccessMatrixModel => {
  return {
    [resourceType.Hosts]: RBACRoleType.Read,
    [resourceType.HostSummaryStatistic]: RBACRoleType.Read,
    [resourceType.CountriesByHosts]: RBACRoleType.Read,
    [resourceType.StatisticComparisonByDays]: RBACRoleType.AllAccess,
    [resourceType.Trends]: RBACRoleType.AllAccess,
    [resourceType.TrendsHosts]: RBACRoleType.AllAccess,
  };
};

export const getRBACAccessMatrixByUserRole = (
  role: UserRole,
): RBACAccessMatrixModel => {
  const getMatrix = {
    [UserRole.Admin]: getAdminSupervisorRBACMatrix,
    [UserRole.Supervisor]: getAdminSupervisorRBACMatrix,
    [UserRole.Manager]: getManagerRBACMatrix,
    [UserRole.Moderator]: getModeratorRBACMatrix,
    [UserRole.Advertiser]: getAdvertiserRBACMatrix,
    [UserRole.Analyst]: getAnalystRBACMatrix,
    [UserRole.DemoVisitor]: getDemoVisitorRBACMatrix,
    [UserRole.Publisher]: getPublisherVisitorRBACMatrix,
  }[role];

  return getMatrix && getMatrix();
};
